import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, PlusCircle, Upload, Pencil } from "lucide-react";
import { Seo } from "../components/Seo";
import { ClubLogo } from "../components/ClubLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0B1520]/60 px-4 py-3 text-sm text-[#F7F5F0] placeholder-[#5C7285] focus:outline-none focus:border-[#E5AC3C] focus:ring-1 focus:ring-[#E5AC3C] transition-colors";

const TEAMS = ["1st XI", "2nd XI", "Midweek XI", "Sunday XI", "U9", "U11", "U13", "U15", "Girls U11", "Girls U13", "Women's"];
const PHOTO_CATEGORIES = ["Senior", "Juniors", "Girls", "Ground & Pavilion"];

const sponsorLogoSrc = (logo) => (logo && logo.startsWith("/assets") ? logo : `${API}/files/${logo}`);

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("news");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [news, setNews] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [fixtures, setFixtures] = useState({ upcoming: [], recent: [] });
  const [events, setEvents] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [post, setPost] = useState({ title: "", category: "Club News", summary: "", body: "" });
  const [fixture, setFixture] = useState({ team: "1st XI", opponent: "", competition: "", date: "", time: "", venue: "Home", status: "scheduled", result: "" });
  const [event, setEvent] = useState({ title: "", category: "Club", date: "", time: "", description: "" });
  const [editingEventId, setEditingEventId] = useState(null);
  const [photoForm, setPhotoForm] = useState({ caption: "", category: "Ground & Pavilion" });
  const [photoFile, setPhotoFile] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({ name: "", link: "" });
  const [sponsorFile, setSponsorFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const sponsorInputRef = useRef(null);

  const loadAll = () => {
    axios.get(`${API}/news`).then((r) => setNews(r.data)).catch(() => {});
    axios.get(`${API}/admin/enquiries`).then((r) => setEnquiries(r.data)).catch(() => {});
    axios.get(`${API}/admin/bookings`).then((r) => setBookings(r.data)).catch(() => {});
    axios.get(`${API}/fixtures`).then((r) => setFixtures(r.data)).catch(() => {});
    axios.get(`${API}/events`).then((r) => setEvents(r.data.all || [])).catch(() => {});
    axios.get(`${API}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
    axios.get(`${API}/sponsors`).then((r) => setSponsors(r.data)).catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then((r) => { setUser(r.data); loadAll(); })
      .catch(() => setUser(null));
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, login);
      setUser(res.data);
      loadAll();
      toast.success(`Welcome back, ${res.data.name}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    }
  };

  const doLogout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(null);
  };

  const submit = (endpoint, payload, reset, label) => async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/${endpoint}`, payload);
      toast.success(label);
      reset();
      loadAll();
    } catch {
      toast.error("Could not save");
    }
  };

  const remove = (endpoint, id) => async () => {
    try {
      await axios.delete(`${API}/admin/${endpoint}/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Could not delete");
    }
  };

  const emptyEvent = { title: "", category: "Club", date: "", time: "", description: "" };

  const saveEvent = async (e) => {
    e.preventDefault();
    try {
      if (editingEventId) {
        await axios.put(`${API}/admin/events/${editingEventId}`, event);
        toast.success("Event updated — the calendar now shows the new details");
      } else {
        await axios.post(`${API}/admin/events`, event);
        toast.success("Event saved — it's now on the calendar");
      }
      setEditingEventId(null);
      setEvent(emptyEvent);
      loadAll();
    } catch {
      toast.error("Could not save");
    }
  };

  const startEditEvent = (ev) => {
    setEditingEventId(ev.id);
    setEvent({ title: ev.title, category: ev.category, date: ev.date, time: ev.time || "", description: ev.description || "" });
  };

  const upload = (endpoint, buildFormData, reset, label) => async (e) => {
    e.preventDefault();
    const fd = buildFormData();
    if (!fd) return;
    setUploading(true);
    try {
      await axios.post(`${API}/admin/${endpoint}`, fd);
      toast.success(label);
      reset();
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadPhoto = upload(
    "photos",
    () => {
      if (!photoFile) { toast.error("Choose an image first"); return null; }
      const fd = new FormData();
      fd.append("file", photoFile);
      fd.append("caption", photoForm.caption);
      fd.append("category", photoForm.category);
      return fd;
    },
    () => { setPhotoFile(null); setPhotoForm({ caption: "", category: "Ground & Pavilion" }); if (photoInputRef.current) photoInputRef.current.value = ""; },
    "Photo added to the gallery"
  );

  const uploadSponsor = upload(
    "sponsors",
    () => {
      if (!sponsorFile) { toast.error("Choose a logo image first"); return null; }
      if (!sponsorForm.name.trim()) { toast.error("Sponsor name is required"); return null; }
      const fd = new FormData();
      fd.append("file", sponsorFile);
      fd.append("name", sponsorForm.name);
      fd.append("link", sponsorForm.link);
      return fd;
    },
    () => { setSponsorFile(null); setSponsorForm({ name: "", link: "" }); if (sponsorInputRef.current) sponsorInputRef.current.value = ""; },
    "Sponsor added"
  );

  if (user === undefined) {
    return <main className="pt-40 pb-24 text-center text-[#5C7285]" data-testid="admin-loading">Loading…</main>;
  }

  if (user === null) {
    return (
      <main className="min-h-screen flex items-center justify-center px-5 pt-20" data-testid="admin-login-page">
        <Seo title="Club Admin — Whitley Bay Cricket Club" description="Admin login for Whitley Bay Cricket Club." />
        <form onSubmit={doLogin} className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#122030] p-8" data-testid="admin-login-form">
          <div className="flex justify-center mb-6"><ClubLogo size={52} /></div>
          <h1 className="font-head text-xl font-bold text-center">Club Admin</h1>
          <p className="mt-1 text-xs text-[#5C7285] text-center">Sign in to manage news, fixtures, events, photos, sponsors and enquiries</p>
          <input required type="email" className={`${inputCls} mt-6`} placeholder="Email" value={login.email}
            onChange={(e) => setLogin({ ...login, email: e.target.value })} data-testid="admin-email-input" />
          <input required type="password" className={`${inputCls} mt-3`} placeholder="Password" value={login.password}
            onChange={(e) => setLogin({ ...login, password: e.target.value })} data-testid="admin-password-input" />
          <button type="submit" data-testid="admin-login-button"
            className="mt-5 w-full bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
            Sign in
          </button>
        </form>
      </main>
    );
  }

  const tabs = [
    ["news", `News (${news.length})`],
    ["fixtures", `Fixtures (${fixtures.upcoming.length + fixtures.recent.length})`],
    ["events", `Events (${events.length})`],
    ["photos", `Photos (${photos.length})`],
    ["sponsors", `Sponsors (${sponsors.length})`],
    ["enquiries", `Enquiries (${enquiries.length})`],
    ["bookings", `Bookings (${bookings.length})`],
  ];

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-32 pb-24" data-testid="admin-dashboard">
      <Seo title="Club Admin — Whitley Bay Cricket Club" description="Admin dashboard for Whitley Bay Cricket Club." />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">Club Admin</h1>
          <p className="text-sm text-[#5C7285]">Signed in as {user.email}</p>
        </div>
        <button onClick={doLogout} data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 border border-white/20 hover:border-[#E5AC3C] hover:text-[#E5AC3C] text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
          <LogOut size={15} /> Sign out
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
            className={`text-xs font-bold uppercase tracking-[0.12em] px-4 py-2 rounded-full border transition-colors ${
              tab === key ? "bg-[#E5AC3C] text-[#0B1520] border-[#E5AC3C]" : "border-white/15 text-[#9BB0C1] hover:border-[#E5AC3C] hover:text-[#E5AC3C]"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "news" && (
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
          <form onSubmit={submit("news", post, () => setPost({ title: "", category: "Club News", summary: "", body: "" }), "News post published")}
            className="rounded-2xl border border-white/10 bg-[#122030] p-6 h-fit" data-testid="admin-news-form">
            <h2 className="font-head text-lg font-bold inline-flex items-center gap-2"><PlusCircle size={18} className="text-[#E5AC3C]" /> New post</h2>
            <input required minLength={3} className={`${inputCls} mt-4`} placeholder="Title" value={post.title}
              onChange={(e) => setPost({ ...post, title: e.target.value })} data-testid="admin-news-title-input" />
            <select className={`${inputCls} mt-3`} value={post.category}
              onChange={(e) => setPost({ ...post, category: e.target.value })} data-testid="admin-news-category-select">
              {["Club News", "Honours", "Juniors", "Seniors", "Social", "Sponsorship"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <textarea required minLength={3} rows={2} className={`${inputCls} mt-3`} placeholder="Summary" value={post.summary}
              onChange={(e) => setPost({ ...post, summary: e.target.value })} data-testid="admin-news-summary-input" />
            <textarea rows={4} className={`${inputCls} mt-3`} placeholder="Body (optional)" value={post.body}
              onChange={(e) => setPost({ ...post, body: e.target.value })} data-testid="admin-news-body-input" />
            <button type="submit" data-testid="admin-news-publish-button"
              className="mt-4 w-full bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
              Publish
            </button>
          </form>
          <div className="space-y-3">
            {news.map((n) => (
              <div key={n.id} className="rounded-2xl border border-white/10 bg-[#122030] p-5 flex items-start justify-between gap-4" data-testid={`admin-news-item-${n.id}`}>
                <div>
                  <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C]">{n.category} · {n.date}</span>
                  <h3 className="mt-1 font-head font-bold">{n.title}</h3>
                  <p className="mt-1 text-sm text-[#9BB0C1] line-clamp-2">{n.summary}</p>
                </div>
                <button onClick={remove("news", n.id)} data-testid={`admin-news-delete-${n.id}`}
                  className="text-[#5C7285] hover:text-red-400 transition-colors p-1" aria-label="Delete post">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "fixtures" && (
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
          <form onSubmit={submit("fixtures", fixture, () => setFixture({ team: "1st XI", opponent: "", competition: "", date: "", time: "", venue: "Home", status: "scheduled", result: "" }), "Fixture saved")}
            className="rounded-2xl border border-white/10 bg-[#122030] p-6 h-fit" data-testid="admin-fixture-form">
            <h2 className="font-head text-lg font-bold inline-flex items-center gap-2"><PlusCircle size={18} className="text-[#E5AC3C]" /> New fixture</h2>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <select className={inputCls} value={fixture.team} onChange={(e) => setFixture({ ...fixture, team: e.target.value })} data-testid="admin-fixture-team-select">
                {TEAMS.map((t) => <option key={t}>{t}</option>)}
              </select>
              <select className={inputCls} value={fixture.venue} onChange={(e) => setFixture({ ...fixture, venue: e.target.value })} data-testid="admin-fixture-venue-select">
                <option>Home</option><option>Away</option>
              </select>
            </div>
            <input required minLength={2} className={`${inputCls} mt-3`} placeholder="Opposition (e.g. Tynemouth CC)" value={fixture.opponent}
              onChange={(e) => setFixture({ ...fixture, opponent: e.target.value })} data-testid="admin-fixture-opponent-input" />
            <input className={`${inputCls} mt-3`} placeholder="Competition (e.g. NTCL Division 3)" value={fixture.competition}
              onChange={(e) => setFixture({ ...fixture, competition: e.target.value })} data-testid="admin-fixture-competition-input" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <input required type="date" className={inputCls} value={fixture.date}
                onChange={(e) => setFixture({ ...fixture, date: e.target.value })} data-testid="admin-fixture-date-input" />
              <input className={inputCls} placeholder="Time (e.g. 13:00)" value={fixture.time}
                onChange={(e) => setFixture({ ...fixture, time: e.target.value })} data-testid="admin-fixture-time-input" />
            </div>
            <select className={`${inputCls} mt-3`} value={fixture.status} onChange={(e) => setFixture({ ...fixture, status: e.target.value })} data-testid="admin-fixture-status-select">
              <option value="scheduled">Scheduled (upcoming)</option>
              <option value="completed">Completed (result)</option>
            </select>
            {fixture.status === "completed" && (
              <input className={`${inputCls} mt-3`} placeholder="Result (e.g. Won by 5 wickets)" value={fixture.result}
                onChange={(e) => setFixture({ ...fixture, result: e.target.value })} data-testid="admin-fixture-result-input" />
            )}
            <button type="submit" data-testid="admin-fixture-save-button"
              className="mt-4 w-full bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
              Save fixture
            </button>
          </form>
          <div className="space-y-3">
            {[...fixtures.upcoming, ...fixtures.recent].length === 0 && <p className="text-[#5C7285]" data-testid="admin-fixtures-empty">No fixtures yet — add the first one.</p>}
            {[...fixtures.upcoming, ...fixtures.recent].map((f) => (
              <div key={f.id} className="rounded-2xl border border-white/10 bg-[#122030] p-5 flex items-start justify-between gap-4" data-testid={`admin-fixture-item-${f.id}`}>
                <div>
                  <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C]">{f.team} · {f.date}{f.time ? ` · ${f.time}` : ""} · {f.venue}</span>
                  <h3 className="mt-1 font-head font-bold">vs {f.opponent}</h3>
                  {f.result && <p className="mt-1 text-sm text-[#2A8C68]">{f.result}</p>}
                </div>
                <button onClick={remove("fixtures", f.id)} data-testid={`admin-fixture-delete-${f.id}`}
                  className="text-[#5C7285] hover:text-red-400 transition-colors p-1" aria-label="Delete fixture">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "events" && (
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
          <form onSubmit={saveEvent}
            className="rounded-2xl border border-white/10 bg-[#122030] p-6 h-fit" data-testid="admin-event-form">
            <h2 className="font-head text-lg font-bold inline-flex items-center gap-2"><PlusCircle size={18} className="text-[#E5AC3C]" /> {editingEventId ? "Edit event" : "New event"}</h2>
            <p className="mt-2 text-xs text-[#5C7285]">Saved events appear on the club calendar (Events page) on the date you pick.</p>
            <input required minLength={3} className={`${inputCls} mt-4`} placeholder="Title (e.g. Summer BBQ)" value={event.title}
              onChange={(e) => setEvent({ ...event, title: e.target.value })} data-testid="admin-event-title-input" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <select className={inputCls} value={event.category} onChange={(e) => setEvent({ ...event, category: e.target.value })} data-testid="admin-event-category-select">
                {["Social", "Juniors", "Club", "Cricket"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <input required type="date" className={inputCls} value={event.date}
                onChange={(e) => setEvent({ ...event, date: e.target.value })} data-testid="admin-event-date-input" />
            </div>
            <input className={`${inputCls} mt-3`} placeholder="Time (e.g. 18:30, optional)" value={event.time}
              onChange={(e) => setEvent({ ...event, time: e.target.value })} data-testid="admin-event-time-input" />
            <textarea rows={3} className={`${inputCls} mt-3`} placeholder="Description (optional)" value={event.description}
              onChange={(e) => setEvent({ ...event, description: e.target.value })} data-testid="admin-event-description-input" />
            <button type="submit" data-testid="admin-event-save-button"
              className="mt-4 w-full bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
              {editingEventId ? "Update event" : "Save event"}
            </button>
            {editingEventId && (
              <button type="button" onClick={() => { setEditingEventId(null); setEvent(emptyEvent); }} data-testid="admin-event-cancel-edit"
                className="mt-2 w-full border border-white/15 hover:border-[#E5AC3C] text-[#9BB0C1] hover:text-[#E5AC3C] font-semibold px-6 py-2.5 rounded-full text-sm transition-colors">
                Cancel editing
              </button>
            )}
          </form>
          <div className="space-y-3">
            {events.length === 0 && <p className="text-[#5C7285]" data-testid="admin-events-empty">No events yet — add the first one.</p>}
            {events.map((ev) => (
              <div key={ev.id} className="rounded-2xl border border-white/10 bg-[#122030] p-5 flex items-start justify-between gap-4" data-testid={`admin-event-item-${ev.id}`}>
                <div>
                  <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C]">{ev.category} · {ev.date}{ev.time ? ` · ${ev.time}` : ""}</span>
                  <h3 className="mt-1 font-head font-bold">{ev.title}</h3>
                  {ev.description && <p className="mt-1 text-sm text-[#9BB0C1] line-clamp-2">{ev.description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEditEvent(ev)} data-testid={`admin-event-edit-${ev.id}`}
                    className="text-[#5C7285] hover:text-[#E5AC3C] transition-colors p-1" aria-label="Edit event">
                    <Pencil size={16} />
                  </button>
                  <button onClick={remove("events", ev.id)} data-testid={`admin-event-delete-${ev.id}`}
                    className="text-[#5C7285] hover:text-red-400 transition-colors p-1" aria-label="Delete event">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "photos" && (
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
          <form onSubmit={uploadPhoto} className="rounded-2xl border border-white/10 bg-[#122030] p-6 h-fit" data-testid="admin-photo-form">
            <h2 className="font-head text-lg font-bold inline-flex items-center gap-2"><Upload size={18} className="text-[#E5AC3C]" /> Add gallery photo</h2>
            <p className="mt-2 text-xs text-[#5C7285]">JPG, PNG or WebP, up to 10 MB. Appears on the public Gallery page instantly.</p>
            <input required type="file" accept="image/jpeg,image/png,image/webp,image/gif" ref={photoInputRef}
              className={`${inputCls} mt-4 file:mr-4 file:rounded-full file:border-0 file:bg-[#E5AC3C] file:px-4 file:py-1.5 file:text-xs file:font-bold file:text-[#0B1520]`}
              onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} data-testid="admin-photo-file-input" />
            <input className={`${inputCls} mt-3`} placeholder="Caption (e.g. U13s at Tynemouth)" value={photoForm.caption}
              onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })} data-testid="admin-photo-caption-input" />
            <select className={`${inputCls} mt-3`} value={photoForm.category}
              onChange={(e) => setPhotoForm({ ...photoForm, category: e.target.value })} data-testid="admin-photo-category-select">
              {PHOTO_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button type="submit" disabled={uploading} data-testid="admin-photo-upload-button"
              className="mt-4 w-full bg-[#E5AC3C] hover:bg-[#F3C35B] disabled:opacity-60 text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
              {uploading ? "Uploading…" : "Upload photo"}
            </button>
          </form>
          <div>
            {photos.length === 0 && <p className="text-[#5C7285]" data-testid="admin-photos-empty">No uploaded photos yet. The gallery already shows the club's original photo set — anything you add here appears alongside it.</p>}
            <div className="grid grid-cols-2 gap-3">
              {photos.map((p) => (
                <div key={p.id} className="rounded-2xl border border-white/10 bg-[#122030] overflow-hidden" data-testid={`admin-photo-item-${p.id}`}>
                  <img src={`${API}/files/${p.storage_path}`} alt={p.caption} className="w-full h-32 object-cover" loading="lazy" />
                  <div className="p-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C]">{p.category}</span>
                      <p className="text-xs text-[#F7F5F0] truncate">{p.caption}</p>
                    </div>
                    <button onClick={remove("photos", p.id)} data-testid={`admin-photo-delete-${p.id}`}
                      className="text-[#5C7285] hover:text-red-400 transition-colors p-1 shrink-0" aria-label="Delete photo">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "sponsors" && (
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
          <form onSubmit={uploadSponsor} className="rounded-2xl border border-white/10 bg-[#122030] p-6 h-fit" data-testid="admin-sponsor-form">
            <h2 className="font-head text-lg font-bold inline-flex items-center gap-2"><Upload size={18} className="text-[#E5AC3C]" /> Add sponsor logo</h2>
            <p className="mt-2 text-xs text-[#5C7285]">Shown in "Our club partners" on the Sponsorship page. PNG/WebP with transparent or white background looks best.</p>
            <input required minLength={2} className={`${inputCls} mt-4`} placeholder="Sponsor name (e.g. Bilash)" value={sponsorForm.name}
              onChange={(e) => setSponsorForm({ ...sponsorForm, name: e.target.value })} data-testid="admin-sponsor-name-input" />
            <input className={`${inputCls} mt-3`} placeholder="Website link (optional, https://…)" value={sponsorForm.link}
              onChange={(e) => setSponsorForm({ ...sponsorForm, link: e.target.value })} data-testid="admin-sponsor-link-input" />
            <input required type="file" accept="image/jpeg,image/png,image/webp,image/gif" ref={sponsorInputRef}
              className={`${inputCls} mt-3 file:mr-4 file:rounded-full file:border-0 file:bg-[#E5AC3C] file:px-4 file:py-1.5 file:text-xs file:font-bold file:text-[#0B1520]`}
              onChange={(e) => setSponsorFile(e.target.files?.[0] || null)} data-testid="admin-sponsor-file-input" />
            <button type="submit" disabled={uploading} data-testid="admin-sponsor-upload-button"
              className="mt-4 w-full bg-[#E5AC3C] hover:bg-[#F3C35B] disabled:opacity-60 text-[#0B1520] font-bold px-6 py-3 rounded-full text-sm transition-colors">
              {uploading ? "Uploading…" : "Add sponsor"}
            </button>
          </form>
          <div className="space-y-3">
            {sponsors.map((s) => (
              <div key={s.id} className="rounded-2xl border border-white/10 bg-[#122030] p-4 flex items-center justify-between gap-4" data-testid={`admin-sponsor-item-${s.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <span className="rounded-xl bg-white p-2 shrink-0 inline-flex">
                    <img src={sponsorLogoSrc(s.logo)} alt={s.name} className="h-10 w-auto" loading="lazy" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-head font-bold text-sm truncate">{s.name}</p>
                    {s.link && <p className="text-xs text-[#5C7285] truncate">{s.link}</p>}
                  </div>
                </div>
                <button onClick={remove("sponsors", s.id)} data-testid={`admin-sponsor-delete-${s.id}`}
                  className="text-[#5C7285] hover:text-red-400 transition-colors p-1 shrink-0" aria-label="Delete sponsor">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "enquiries" && (
        <div className="space-y-3" data-testid="admin-enquiries-list">
          {enquiries.length === 0 && <p className="text-[#5C7285]">No enquiries yet.</p>}
          {enquiries.map((e) => (
            <div key={e.id} className="rounded-2xl border border-white/10 bg-[#122030] p-5" data-testid={`admin-enquiry-${e.id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5C7285]">
                <span className="font-bold text-[#E5AC3C] uppercase tracking-[0.1em]">{e.kind}</span>
                <span>{e.name} · {e.email}</span>
                <span>{(e.created_at || "").slice(0, 10)}</span>
              </div>
              <h3 className="mt-2 font-head font-bold">{e.subject}</h3>
              <p className="mt-1 text-sm text-[#9BB0C1]">{e.message}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="space-y-3" data-testid="admin-bookings-list">
          {bookings.length === 0 && <p className="text-[#5C7285]">No booking requests yet.</p>}
          {bookings.map((b) => (
            <div key={b.id} className="rounded-2xl border border-white/10 bg-[#122030] p-5" data-testid={`admin-booking-${b.id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5C7285]">
                <span className="font-bold text-[#E5AC3C] uppercase tracking-[0.1em]">{b.facility}</span>
                <span>{b.name} · {b.email}{b.phone ? ` · ${b.phone}` : ""}</span>
              </div>
              <h3 className="mt-2 font-head font-bold">{b.date}{b.time ? ` · ${b.time}` : ""}</h3>
              {b.details && <p className="mt-1 text-sm text-[#9BB0C1]">{b.details}</p>}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
